import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Baby, Heart, Apple, Syringe, TrendingUp, Activity,
  Calendar, ShieldAlert, ChevronRight, Sparkles, Clock,
} from 'lucide-react';
import { api, relativeTime, STATUS_PALETTE } from './api';
import ChildSetup from './ChildSetup';
import { useSafeBack } from '@/utils/useSafeBack';

/**
 * ChildrensHub — entry surface for the pediatric assistant.
 *
 * - Top: persistent Snapshot card (name, age, last checkin, status, next vaccine)
 * - Today's Focus: 1-2 dynamic priorities
 * - Primary CTA: "Check in today"
 * - Secondary CTA: "Something feels off?" (Quick Check)
 * - Module grid: Daily Check-in / Immunization / Growth / Nutrition / Behaviour
 *
 * If no child profile exists yet, route to the first-run setup screen.
 */
const ChildrensHub = () => {
  const navigate = useNavigate();
  const handleBack = useSafeBack('/family-health');
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    api.snapshot()
      .then((res) => { if (!cancel) setSnap(res); })
      .catch(() => { if (!cancel) setSnap({ has_profile: false }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-white flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading your child's snapshot…</div>
      </div>
    );
  }

  if (!snap || !snap.has_profile) {
    return <ChildSetup onCreated={(child) => api.snapshot().then(setSnap)} />;
  }

  const { child, last_checkin_at, last_checkin_score, checkin_status, next_vaccine, overdue_vaccines, focus } = snap;
  const statusPal = STATUS_PALETTE[checkin_status] || STATUS_PALETTE['Not checked yet'];
  const hasOverdue = overdue_vaccines && overdue_vaccines.length > 0;

  const modules = [
    {
      key: 'checkin',
      icon: Heart,
      title: 'Daily check-in',
      desc: 'A 60-second pulse on appetite, sleep, mood and symptoms.',
      cta: 'Check in today',
      route: '/childrens-health/checkin',
      iconBg: 'bg-rose-100 text-rose-700',
      hover: 'hover:border-rose-300',
    },
    {
      key: 'immunization',
      icon: Syringe,
      title: 'Immunisation',
      desc: hasOverdue ? `${overdue_vaccines.length} overdue · ${next_vaccine?.name || 'on track'}` : (next_vaccine ? `Next: ${next_vaccine.name} (in ${next_vaccine.delta_months}mo)` : 'All up to date'),
      cta: 'Open schedule',
      route: '/childrens-health/immunization',
      iconBg: 'bg-blue-100 text-blue-700',
      hover: 'hover:border-blue-300',
      pill: hasOverdue ? { text: `${overdue_vaccines.length} overdue`, cls: 'bg-rose-100 text-rose-700' } : null,
    },
    {
      key: 'growth',
      icon: TrendingUp,
      title: 'Growth & development',
      desc: 'Update height & weight to see trend bands and recheck windows.',
      cta: 'Update growth today',
      route: '/childrens-health/growth',
      iconBg: 'bg-emerald-100 text-emerald-700',
      hover: 'hover:border-emerald-300',
    },
    {
      key: 'nutrition',
      icon: Apple,
      title: 'Nutrition guidance',
      desc: 'Log meals — get age-aware patterns (protein, fruit, veg, sweets).',
      cta: "See today's nutrition focus",
      route: '/childrens-health/nutrition',
      iconBg: 'bg-amber-100 text-amber-700',
      hover: 'hover:border-amber-300',
    },
    {
      key: 'behaviour',
      icon: Activity,
      title: 'Behaviour & emotional health',
      desc: 'Note tantrums, mood changes, and sleep-linked behaviour.',
      cta: 'Log behaviour today',
      route: '/childrens-health/behaviour',
      iconBg: 'bg-purple-100 text-purple-700',
      hover: 'hover:border-purple-300',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-white to-white pb-24" data-testid="childrens-hub">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4" data-testid="childrens-hub-back">
          <ArrowLeft className="h-4 w-4" />
          Back to Family Health
        </button>

        <header className="mb-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center shrink-0">
              <Baby className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">Children's Health</h1>
              <p className="text-xs text-gray-500 mt-0.5">A calm pediatric assistant — daily, age-aware, no overwhelm.</p>
            </div>
          </div>
        </header>

        {/* CHILD SNAPSHOT CARD */}
        <section className={`rounded-2xl border-2 ${statusPal.border} ${statusPal.bg} p-5 mb-4`} data-testid="childrens-snapshot">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1">Today's snapshot</p>
              <h2 className="text-2xl font-bold text-gray-900 leading-tight" data-testid="snapshot-name">{child.name}</h2>
              <p className="text-sm text-gray-600 mt-0.5">{child.age_label} · <span className="capitalize">{child.age_band}</span></p>
            </div>
            <div className="text-right shrink-0">
              <div className={`inline-flex items-center gap-1.5 ${statusPal.text} text-sm font-bold`}>
                <span className={`w-2 h-2 rounded-full ${statusPal.dot}`} aria-hidden="true" />
                <span data-testid="snapshot-status">{checkin_status}</span>
              </div>
              {last_checkin_at && (
                <p className="text-[11px] text-gray-500 mt-1 inline-flex items-center gap-1 justify-end">
                  <Clock className="h-3 w-3" />
                  Last check {relativeTime(last_checkin_at)}{typeof last_checkin_score === 'number' ? ` · ${last_checkin_score}/100` : ''}
                </p>
              )}
            </div>
          </div>

          {next_vaccine && (
            <div className="rounded-xl bg-white/80 border border-white/60 p-3 mb-3 flex items-center gap-3" data-testid="snapshot-next-vaccine">
              <Calendar className="h-4 w-4 text-blue-700 shrink-0" />
              <p className="text-sm text-gray-800 flex-1">
                <span className="font-semibold">Upcoming vaccine:</span> {next_vaccine.name}
                <span className="text-gray-500"> · in ~{Math.max(0, next_vaccine.delta_months)} mo</span>
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/childrens-health/checkin')}
            className="w-full bg-gradient-to-br from-sky-500 to-blue-500 hover:brightness-110 text-white font-semibold rounded-xl px-5 py-3 shadow-sm transition-all"
            data-testid="snapshot-checkin-cta"
          >
            Check in today →
          </button>
        </section>

        {/* TODAY'S FOCUS */}
        {focus && focus.length > 0 && (
          <section className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5 mb-4" data-testid="todays-focus">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-amber-600" />
              <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-widest">Today's focus</p>
            </div>
            <ul className="space-y-2">
              {focus.map((f, i) => (
                <li key={f} className="text-sm text-gray-800 leading-relaxed flex items-start gap-2">
                  <span className="text-amber-600 font-bold shrink-0">{i + 1}.</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* QUICK CHECK CTA — urgency surface */}
        <button
          onClick={() => navigate('/childrens-health/quick-check')}
          className="w-full mb-5 rounded-2xl bg-white border-2 border-rose-200 hover:border-rose-300 p-4 text-left flex items-center gap-3 transition-all"
          data-testid="quick-check-cta"
        >
          <span className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
            <ShieldAlert className="h-5 w-5 text-rose-700" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-gray-900">Something feels off?</span>
            <span className="block text-xs text-gray-600 mt-0.5">3-question urgency triage — no immediate concern, monitor, or seek care.</span>
          </span>
          <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
        </button>

        {/* MODULES */}
        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-3 px-1">Care modules</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {modules.map((m) => {
            const Mi = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => navigate(m.route)}
                className={`text-left rounded-2xl bg-white border-2 border-gray-100 ${m.hover} p-4 transition-all`}
                data-testid={`module-${m.key}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className={`w-9 h-9 rounded-lg ${m.iconBg} flex items-center justify-center shrink-0`}>
                    <Mi className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-base font-bold text-gray-900 leading-tight">{m.title}</h3>
                      {m.pill && (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${m.pill.cls}`}>{m.pill.text}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">{m.desc}</p>
                  </div>
                </div>
                <p className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1 mt-1.5">
                  {m.cta} →
                </p>
              </button>
            );
          })}
        </div>

        <p className="text-[11px] text-gray-400 text-center mt-6">
          HealthLoop supports awareness — it does not diagnose or replace pediatric care.
        </p>
      </div>
    </div>
  );
};

export default ChildrensHub;
